import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { Typography, Chip } from '../../../components/Wrappers/Wrappers.jsx';
import DiscoverableToggleCard from './discoverableToggleCard';
import PhotoUpload from './PhotoUpload';
import useEcalWorldProfile from './useEcalWorldProfile';
import useCoverPhoto from './useCoverPhoto';
import useSaveFlash from './useSaveFlash';
import usePrimaryDisplay from './usePrimaryDisplay';
import { useStyles } from './listingSection.styles';

function getListingStatus (isPublic, hasPrimaryDisplay) {
    if (!isPublic) {
        return { status: 'notListed', label: 'Not listed' };
    }
    if (!hasPrimaryDisplay) {
        return { status: 'noPrimary', label: 'Primary Display not set' };
    }
    return { status: 'listed', label: 'Listed on ECAL World' };
}

function ListingSection ({ publisher, sectionRef }) {
    const {
        isPublic,
        confirmOpen,
        tooltipTitle,
        dialogTitle,
        dialogText,
        handleToggle,
        handleConfirm,
        handleCancel,
        renderErrorDialog: renderToggleErrorDialog,
    } = useEcalWorldProfile({ publisher });

    const {
        coverPhoto,
        handleUpload,
        handleDelete,
        renderErrorDialog: renderCoverPhotoErrorDialog,
    } = useCoverPhoto();

    const { saved, flashSaved } = useSaveFlash();
    const { primaryDisplayChecked, hasPrimaryDisplay, checkPrimaryDisplay } = usePrimaryDisplay();
    // Assume no primary display until checked, so any flicker clears a warning rather than raising one.
    const { status, label } = getListingStatus(isPublic, primaryDisplayChecked && hasPrimaryDisplay);
    const classes = useStyles({ status });

    async function handleConfirmWithFlash () {
        await handleConfirm();
        // Re-check so the badge reflects the latest primary-display state.
        await checkPrimaryDisplay();
        flashSaved();
    }

    async function handleUploadWithFlash (dataUrl, file) {
        await handleUpload(dataUrl, file);
        flashSaved();
    }

    async function handleDeleteWithFlash () {
        await handleDelete();
        flashSaved();
    }

    function renderStatusBadge () {
        return (
            <Chip
                size={'small'}
                label={
                    <React.Fragment>
                        <span className={classes.statusDot} />
                        <span className={classes.statusText}>{label}</span>
                    </React.Fragment>
                }
                className={classes.statusBadge}
            />
        );
    }

    return (
        <div className={classes.card} ref={sectionRef}>
            <div className={classes.headingRow}>
                <Typography variant={'h5'} weight={'bold'} className={classes.heading}>
                    Your ECAL World listing
                </Typography>
                { renderStatusBadge() }
            </div>

            <Typography variant={'body1'} className={classes.description}>
                Control whether {publisher?.name || 'your organisation'} appears on ECAL World and how
                your tile looks to consumers.
            </Typography>

            <DiscoverableToggleCard
                isPublic={isPublic}
                confirmOpen={confirmOpen}
                tooltipTitle={tooltipTitle}
                dialogTitle={dialogTitle}
                dialogText={dialogText}
                onToggle={handleToggle}
                onConfirm={handleConfirmWithFlash}
                onCancel={handleCancel}
            />

            <PhotoUpload
                title={'Upload a Cover Image'}
                description={'This image will appear on your ECAL World profile.'}
                imageSource={coverPhoto}
                onUpload={handleUploadWithFlash}
                onDelete={handleDeleteWithFlash}
            />

            <Typography
                variant={'body2'}
                className={classnames(classes.saveFlash, { [classes.saveFlashVisible]: saved })}
            >
                ✓ Saved
            </Typography>

            { renderToggleErrorDialog() }
            { renderCoverPhotoErrorDialog() }
        </div>
    );
}

ListingSection.propTypes = {
    publisher: PropTypes.shape({
        name: PropTypes.string,
        publicAccess: PropTypes.bool,
    }),
    sectionRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

ListingSection.defaultProps = {
    publisher: {},
    sectionRef: null,
};

const ObservedListingSection = observer(ListingSection);
export default ObservedListingSection;
